import React from 'react';
import { Checkbox } from '../../ui/checkbox';
import { RadioGroup, RadioGroupItem } from '../../ui/radio-group';

const AdvancedOptionsViewSection = ({
  submission,
  isDark,
  showStrataOptions = true
}) => {
  const hasCommPref = showStrataOptions && !!submission.sending_preference?.trim();
  const hasPostalOnly = showStrataOptions && submission.sending_preference === 'postal' && submission.delivery_preference === 'postal_only';
  const hasPriority = submission.priority === 'urgent';
  const hasSingleSided = submission.single_sided === true || submission.single_sided === 'true';

  // Nothing to show — hide the entire card
  if (!hasCommPref && !hasPostalOnly && !hasPriority && !hasSingleSided) return null;

  // Matches SendingSection AccordionItem wrapper styling
  const getWrapperClasses = (isOpen) => {
    if (isOpen) {
      return isDark
        ? 'bg-[#000000]'
        : 'bg-white border border-solid border-[1px] border-[#d9d9d9]';
    }
    return isDark
      ? 'bg-[#161515]'
      : 'bg-[#f5f5f5]';
  };

  // Matches SendingSection AccordionTrigger styling
  const getAccordionHeaderClasses = (isOpen) => {
    if (isOpen) {
      return isDark
        ? 'bg-[#1a1a1a] rounded-t-[8px]'
        : 'bg-[#fff5eb] rounded-t-[8px]';
    }
    return isDark
      ? 'bg-[#161515] rounded-[8px]'
      : 'bg-[#f5f5f5] rounded-[8px]';
  };

  const getAccordionTextClasses = () => {
    return `font-medium ${isDark ? 'text-white' : 'text-[#072837]'}`;
  };

  return (
    <div className={`rounded-[10px] p-[20px] flex flex-col gap-[16px] transition-colors duration-200 ${
      isDark ? 'bg-[#000000]' : 'bg-white'
    }`} style={{ boxShadow: '0px 4px 4px rgba(0, 0, 0, 0.11)' }}>

      {/* Title */}
      <div className={`text-[16px] font-semibold transition-colors duration-200 ${
        isDark ? 'text-white' : 'text-[#072837]'
      }`} style={{ fontFamily: "'DM Sans', sans-serif" }}>
        Advanced Options
      </div>

      {/* Accordions */}
      <div className="flex flex-col gap-[8px]">

        {/* Communication Preference Override - Strata only, only when value exists */}
        {hasCommPref && (
        <div className={`rounded-[8px] overflow-hidden transition-colors duration-200 ${getWrapperClasses(true)}`}>
          <div className={`w-full flex flex-row items-center p-[12px] transition-colors duration-200 ${getAccordionHeaderClasses(true)}`}>
            <span className={`text-[16px] transition-colors duration-200 ${getAccordionTextClasses()}`} style={{ fontFamily: "'DM Sans', sans-serif" }}>
              Communication Preference Override
            </span>
          </div>
          <div className={`p-[20px] flex flex-col gap-[16px] transition-colors duration-200 ${
            isDark ? 'bg-[#000000]' : 'bg-white'
          }`}>
            <p className={`m-0 text-[12px] transition-colors duration-200 ${
              isDark ? 'text-[#b3b3b3]' : 'text-[#686868]'
            }`}>This should only ever be used in special circumstances e.g. urgent correspondence to be delivered or under committee instruction. If you are overriding to send to all via email - consider using Front Instead</p>
            <div className='flex items-center gap-[8px]'>
              <Checkbox
                id="sending-preference-view"
                checked={true}
                labelClassName="cursor-default"
                isDark={isDark}
              />
              <span className={`text-[14px] text-capitalize transition-colors duration-200 ${
                isDark ? 'text-white' : 'text-[#072837]'
              }`}>{submission.sending_preference}</span>
            </div>
          </div>
        </div>
        )}

        {/* Sending to Postal recipients only - only when postal_only is set */}
        {hasPostalOnly && (
        <div className={`rounded-[8px] overflow-hidden transition-colors duration-200 ${getWrapperClasses(true)}`}>
          <div className={`w-full flex flex-row items-center p-[12px] transition-colors duration-200 ${getAccordionHeaderClasses(true)}`}>
            <span className={`text-[16px] transition-colors duration-200 ${getAccordionTextClasses()}`} style={{ fontFamily: "'DM Sans', sans-serif" }}>
              Sending to Postal recipients only
            </span>
          </div>
          <div className={`p-[20px] flex flex-col gap-[16px] transition-colors duration-200 ${
            isDark ? 'bg-[#000000]' : 'bg-white'
          }`}>
            <div className='flex items-center gap-[8px]'>
              <Checkbox
                id="postal-only-view"
                checked={true}
                labelClassName="cursor-default"
                isDark={isDark}
              />
              <span className={`text-[14px] transition-colors duration-200 ${
                isDark ? 'text-white' : 'text-[#072837]'
              }`}>Send to recipients who elected post only</span>
            </div>
          </div>
        </div>
        )}

        {/* Priority Post - only when urgent */}
        {hasPriority && (
        <div className={`rounded-[8px] overflow-hidden transition-colors duration-200 ${getWrapperClasses(true)}`}>
          <div className={`w-full flex flex-row items-center p-[12px] transition-colors duration-200 ${getAccordionHeaderClasses(true)}`}>
            <span className={`text-[16px] transition-colors duration-200 ${getAccordionTextClasses()}`} style={{ fontFamily: "'DM Sans', sans-serif" }}>
              Priority Post – additional cost will be incurred
            </span>
          </div>
          <div className={`p-[20px] flex flex-col gap-[16px] transition-colors duration-200 ${
            isDark ? 'bg-[#000000]' : 'bg-white'
          }`}>
            <RadioGroup value="urgent">
              <div className='flex flex-col gap-[14px]'>
                <div className='flex gap-[8px] items-center'>
                  <RadioGroupItem value="urgent" id="priority-urgent-view" />
                  <label htmlFor="priority-urgent-view" className={`text-[16px] m-0 cursor-default transition-colors duration-200 ${
                    isDark ? 'text-white' : 'text-[#072837]'
                  }`}>Urgent (allow 24 hrs)</label>
                </div>
              </div>
            </RadioGroup>
          </div>
        </div>
        )}

        {/* Single-Sided Printing - only when enabled */}
        {hasSingleSided && (
        <div className={`rounded-[8px] overflow-hidden transition-colors duration-200 ${getWrapperClasses(true)}`}>
          <div className={`w-full flex flex-row items-center p-[12px] transition-colors duration-200 ${getAccordionHeaderClasses(true)}`}>
            <span className={`text-[16px] transition-colors duration-200 ${getAccordionTextClasses()}`} style={{ fontFamily: "'DM Sans', sans-serif" }}>
              Single-Sided Printing
            </span>
          </div>
          <div className={`p-[20px] flex flex-col gap-[16px] transition-colors duration-200 ${
            isDark ? 'bg-[#000000]' : 'bg-white'
          }`}>
            <div className='flex items-center gap-[8px]'>
              <Checkbox
                id="single-sided-view"
                checked={true}
                labelClassName="cursor-default"
                isDark={isDark}
              />
              <span className={`text-[14px] transition-colors duration-200 ${
                isDark ? 'text-white' : 'text-[#072837]'
              }`}>Print single-sided</span>
            </div>
          </div>
        </div>
        )}
      </div>
    </div>
  );
};

export default AdvancedOptionsViewSection;
